import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: [
      "By accessing or using The Daily Find service (the \"Service\"), you agree to be bound by these Terms of Service (the \"Terms\"). If you do not agree, do not use the Service.",
    ],
  },
  {
    id: "service",
    title: "2. Service description",
    body: [
      "The Service is an AI-driven affiliate marketing automation platform. It helps you discover trending content, generate short-form videos, manage affiliate product links, route inbound email, and publish content to connected social platforms — subject to the rules of those platforms and applicable law.",
    ],
  },
  {
    id: "responsibilities",
    title: "3. User responsibilities",
    body: [
      "You will comply with the terms of service of every connected platform (TikTok, TikTok Shop, AliExpress, Amazon Associates, Clickbank, Resend, etc.) at all times.",
      "You will not use the Service to publish content that violates platform policy, that infringes intellectual property, that constitutes spam, that misleads consumers, or that targets users under the age of 13.",
      "You are responsible for the accuracy of every claim you publish and for ensuring you have the right to promote every product you link.",
      "You agree to include an affiliate disclosure (`#ad`, `#affiliate`, or equivalent verbal disclosure) on every piece of content that promotes an affiliate product. The Service's compliance lint will flag drafts that omit it, but the final responsibility is yours.",
    ],
  },
  {
    id: "age",
    title: "4. Account requirements",
    body: [
      "You must be at least 18 years old to use the Service.",
      "You agree to provide accurate registration information and to keep your credentials secure. You are responsible for all activity that occurs under your account.",
    ],
  },
  {
    id: "disclosure",
    title: "5. Affiliate disclosure requirement",
    body: [
      "The FTC, TikTok, Amazon Associates Operating Agreement, AliExpress Affiliate Terms, and most major regulators require clear disclosure of material commercial relationships. You will follow these rules even when the Service does not explicitly require it.",
      "You will not use the Service to misrepresent paid endorsements as organic content.",
    ],
  },
  {
    id: "prohibited",
    title: "6. Prohibited uses",
    body: [
      "Weapons, tobacco, alcohol, gambling, adult content, prescription medication, and CBD are not supported product categories on the Service and you may not use the Service to promote them.",
      "Therapeutic / medical claims, guaranteed-return financial claims, fake scarcity (\"only 3 left\", \"today only\" when untrue), and political commentary on niche-restricted accounts (edutainment / history / curiosity) are blocked by the compliance lint and may not be bypassed.",
      "You may not reverse-engineer, scrape, or resell the Service or any of its outputs to third parties without our written permission.",
    ],
  },
  {
    id: "ip",
    title: "7. Intellectual property",
    body: [
      "We retain ownership of the Service, the codebase, the AI pipeline, the UI, and all underlying systems.",
      "You retain ownership of the products you import, the original scripts you write, and the captured analytics for your accounts.",
      "Videos generated by the Service combine your inputs with third-party stock footage (Pexels), AI-synthesized voiceover (OpenAI), and optionally AI-synthesized avatars (HeyGen). You receive a license to use the resulting MP4 for your own affiliate marketing within the terms of those upstream providers. You do not receive copyright over the underlying stock footage or AI-generated assets where local law denies copyright to non-human authors.",
    ],
  },
  {
    id: "termination",
    title: "8. Termination",
    body: [
      "You may delete your account at any time from Settings.",
      "We may suspend or terminate your account if you violate these Terms, if you abuse third-party platforms through the Service, or if continued operation creates legal or operational risk for us. Where reasonable we will give notice and a chance to cure.",
      "On termination, sections relating to intellectual property, liability, governing law, and any unpaid third-party API costs incurred on your behalf survive.",
    ],
  },
  {
    id: "liability",
    title: "9. Disclaimer & limitation of liability",
    body: [
      "The Service is provided \"as is\" without warranties of any kind. We do not guarantee uptime, the accuracy of AI outputs, the availability of upstream platforms (TikTok, Resend, OpenAI, HeyGen, RapidAPI), or the conversion of any affiliate link.",
      "To the maximum extent permitted by law, our aggregate liability for any claim arising out of the Service is limited to the greater of (a) USD 100 or (b) the total fees you paid for the Service in the 12 months preceding the claim.",
      "We are not liable for indirect, incidental, special, or consequential damages, including lost profits.",
    ],
  },
  {
    id: "law",
    title: "10. Governing law",
    body: [
      "These Terms are governed by the laws of the State of Delaware, USA, without regard to its conflict-of-laws principles.",
      "Any dispute will be brought exclusively in the state or federal courts located in Delaware, and you consent to that jurisdiction.",
    ],
  },
  {
    id: "changes",
    title: "11. Changes",
    body: [
      "We may amend these Terms from time to time. Material changes will be announced in-app at least 14 days before they take effect. Continued use after that period constitutes acceptance.",
    ],
  },
  {
    id: "contact",
    title: "12. Contact",
    body: [
      "Questions: hello@thedailyfind.app",
      "Legal notices: legal@thedailyfind.app",
    ],
  },
];

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <Link to="/" className="text-xs mono text-emerald-400 hover:text-emerald-300"
              data-testid="terms-back-home">← thedailyfind.app</Link>
        <h1 className="text-3xl sm:text-4xl font-bold mt-6 mb-2 tracking-tight"
            data-testid="terms-title">Terms of Service</h1>
        <div className="text-xs mono text-zinc-500">Last updated: 23 February 2026</div>

        <div className="mt-6 rounded-md border border-amber-900/60 bg-amber-950/30 px-4 py-3 text-xs text-amber-200">
          This is a starting template for an AI-driven affiliate marketing tool.
          Have a lawyer review it for your jurisdiction before relying on it for compliance.
        </div>

        <div className="mt-8 space-y-7" data-testid="terms-sections">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="text-base font-semibold text-zinc-100 mb-2">{s.title}</h2>
              <div className="space-y-2 text-sm text-zinc-300 leading-relaxed">
                {s.body.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-12 pt-6 border-t border-zinc-800 text-xs text-zinc-500 flex items-center justify-between flex-wrap gap-2">
          <span className="mono">© The Daily Find</span>
          <span className="flex gap-4">
            <Link to="/privacy" className="hover:text-zinc-300" data-testid="terms-footer-privacy">Privacy</Link>
            <a href="mailto:legal@thedailyfind.app" className="hover:text-zinc-300">Contact</a>
          </span>
        </footer>
      </article>
    </div>
  );
}
